/**
 * The seam between a **published tool description** and a **form a person fills in**.
 *
 * `.docs/24` §D3 (B-5) asked where the human-facing label, hint, control and refusal
 * should live, and this file is the answer. It exists because the two audiences are
 * genuinely different and the repository had only ever written for one of them:
 *
 * > *"A client slug, lowercase — 2 to 40 characters of letters, digits and hyphens.
 * > **This is how every tool addresses a client; there is no UUID in the API
 * > surface.**"*
 *
 * That is a correct, useful sentence — addressed to an agent choosing between tools,
 * and printed under a text box at a studio member who wanted to pick Casaturca from
 * a list of seven. `ActionForm` draws 94 of the 98 mutating actions from the
 * published JSON Schema and that design is right; what was missing was a place for
 * the words a person reads.
 *
 * ## The standard, in five lines
 *
 *  1. **The label is a noun phrase somebody would say out loud.** `labelOf` — the
 *     machine name with its underscores spent — is the fallback, not the rule.
 *  2. **The hint is written for the person filling the field in.** The published
 *     description is the *fallback*, because a tool description is addressed to a
 *     caller and a hint is addressed to a reader.
 *  3. **The control is the narrowest one that can express the field.** A closed set is
 *     a picker (clients, studio people, services, enums); a day is a day box; a uuid
 *     is never typed by hand.
 *  4. **A refusal is one whole sentence that says what to do**, and it comes out of
 *     the same entry as the hint, so the guidance and the refusal cannot drift. It
 *     replaces the hint while it is showing — CONTRACT.md §2.8 has one muted line per
 *     field and this repository is not going to grow a second — which is precisely
 *     why it has to carry the guidance rather than restate the field name.
 *  5. **Nothing here rewrites a published description.** This is a second column
 *     beside `catalogue.snap`, never an edit to it: a reviewer reads the snapshot and
 *     an agent reads the descriptions, and neither should move because a form wanted
 *     a shorter sentence.
 *
 * ## Why a partial override here and a whole one in `ACTION_FIELDS`
 *
 * `actionFields.ts` says an explicit entry "**replaces** introspection for that
 * action, whole — a partial override would be a third place a field could come from,
 * and the reason a field is missing from a form is exactly the kind of thing nobody
 * notices". That is right about *fields* and wrong about *words*, and the difference
 * is what the failure looks like: a missing field is invisible, while a missing hint
 * falls back to the schema's own description, which is always there and is never
 * wrong — only badly addressed. So words are keyed per field and fields are not.
 *
 * A key is `"<action>.<field>"` or bare `"<field>"`, and the first that has an answer
 * wins per property — so `client` is written once for all forty-two actions that take
 * one, and `tasks_create.client` could still say something of its own.
 */

import { PRIORITY_WORD, QUEUE_WORD } from './taskWords.js'

export type FieldCopy = {
  /** A noun phrase a person would say. `labelOf(name)` when this is absent. */
  readonly label?: string
  /**
   * The muted line under the control. The schema's own description otherwise.
   *
   * **`null` is deliberate silence**, and it is the answer for a field whose label
   * has already said everything there is to say. `undefined` means "nobody has
   * written one yet" and keeps the published description; `null` means "there is
   * nothing here a person needs" and removes the line. CONTRACT.md §2.8 gives a
   * field one muted line, and spending it on a restatement of the label is how a
   * form of nine fields becomes a wall nobody reads.
   */
  readonly hint?: string | null
  /** What an empty box shows. Never a question — the label already asked one. */
  readonly placeholder?: string
  /** Human words for the members of a closed set, by value. */
  readonly options?: Readonly<Record<string, string>>
  /**
   * The repeater button's words, for a `rows` field — "Add a milestone".
   *
   * Written rather than derived. The derivation was `Add ${label.toLowerCase()}`
   * over the label the field is *drawn* with, and on the Price-it form that came out
   * as **"Add add ons (optional)"** — the optional suffix and `labelOf('add_ons')`
   * together. A button is a verb and a noun and no rule turns a plural field name
   * into one.
   */
  readonly add?: string
  /**
   * The whole sentence a required field is refused with.
   *
   * It says what to put there rather than that something is missing: the client
   * portal already refuses like this — *"A sentence is enough — the studio reads
   * it."* — and the two portals are one product.
   */
  readonly required?: string
}

/* ---------------------------------------------------------------- the entries */

/**
 * The one sentence about invoicing a deposit — the generic form's and `QuoteBuilder`'s.
 *
 * Two surfaces ask the same question: *Send the quote* on a work order's own page,
 * drawn from the schema, and the *New work order* composer, which is hand-written
 * because it prices as it goes. They had the same words by copy-and-paste and they
 * were the same wrong words. One export, two renderings.
 */
export const DEPOSIT_ON_APPROVAL = {
  label: 'Invoice the first milestone the moment they approve',
  hint:
    'On, approving it issues the first milestone straight away as a deposit invoice. ' +
    'Off, every milestone invoices when it is delivered, like the rest.',
} as const

/**
 * The words for a write-off's reason — the generic form's and `InvoiceMoves`' Write off
 * dialog's, for `DEPOSIT_ON_APPROVAL`'s reason: two surfaces, one sentence.
 *
 * The shared `reason` entry says "the client **may** read it", which is true of a
 * task and too weak here: a write-off's reason is returned with the client's own
 * invoice (`InvoiceDetailSchema.write_offs`, `.docs/44-invoice-write-off.md` decision
 * 7), so the label names who can read it and the hint shows the kind of sentence that
 * survives being read by them. No client page prints it yet (report 44, open item) —
 * which is why the words say "can see", not "read it on their invoice page".
 */
export const WRITE_OFF_REASON = {
  label: 'Why, in words to the client',
  hint:
    'The client can see it with their invoice, so write it to them — “The agreed signup fee ' +
    'was $350; this invoice asked for $850.” It stays on the record.',
  required: 'Say why, to the client. They can see it with their invoice.',
} as const

/**
 * Words for a field, by `"<action>.<field>"` or by `"<field>"` alone.
 *
 * Every entry below is a line the manual pass found in front of a person, with the
 * finding beside it. This is a list that grows one screen at a time; a field with no
 * entry is not a gap, it is a field whose published description happened to read
 * well.
 */
/**
 * What "off" means for an invoice's mail, said once for the three fields that have
 * the switch (`invoices_issue`, `retainers_start`, `billing_run`): the invoice is real,
 * nothing is emailed, and there is a way to email it later — without naming the tool.
 */
const QUIET_INVOICE =
  'Off, the invoice is issued and owed exactly as usual and its PDF is ready — nothing ' +
  'is emailed, and the sent date stays empty because nothing left the studio. You can ' +
  'send it from the invoice afterwards.'

export const FIELD_COPY: Readonly<Record<string, FieldCopy>> = {
  /*
   * R3-09. "A client slug, lowercase — 2 to 40 characters… This is how every tool
   * addresses a client; there is no UUID in the API surface." A studio member had to
   * type `casaturca` and was refused for typing `Casaturca`, with seven clients that
   * could have been a list. The control is a picker now; the hint says what the
   * choice means rather than how the API is addressed.
   */
  client: {
    label: 'Client',
    hint: 'Which account this is for. Leave it blank for studio work that belongs to nobody.',
    required: 'Choose the client this is for, or leave it out if it is studio work.',
  },

  /*
   * R3-09. `Delivery / Content / Seo / Odoo` — a naive title-case over the enum, and
   * a description that cites an internal document at a reader who cannot open it:
   * "(ARCHITECTURE.md, The task queue)". `QUEUE_WORD` is the board's own reading of
   * the same seven values, so a queue is called the same thing on a card, in a
   * table, on the detail page and in this form.
   */
  queue: {
    label: 'Queue',
    hint: 'Who works it. Delivery, Content, SEO and Odoo are production; Account and Billing are the two a person normally holds.',
    options: QUEUE_WORD,
    required: 'Choose the queue that will work it.',
  },

  /* Industry pattern: “approval required” / “needs approval” (PMSuite, Asana gate). */
  needs_review: {
    label: 'Approval required',
    hint: 'When on, marking the task done sends it to a person first instead of closing it. Money on the task turns this on whatever you pick.',
  },

  /*
   * R3-09. Free text, and `next Friday` gave the raw Zod "Invalid ISO datetime". The
   * control is a day-and-time box now, so the shape cannot be got wrong at all.
   */
  due_at: {
    label: 'Due',
    hint: 'The day and time it is due, in your own zone. Leave it blank and it has no date.',
  },

  /* R3-09. "The service this is work on — `"video_editing"`." — a code, at a person. */
  service: {
    label: 'Service',
    hint: 'What kind of work it is, off the rate card.',
    required: 'Choose the service this is work on.',
  },
  service_code: {
    label: 'Service',
    hint: 'What kind of work it is, off the rate card. Leave it blank for work that is not against one.',
  },

  /* R3-04. A bare uuid box whose help text was the tool's own parameter documentation. */
  assignee: {
    label: 'Who it goes to',
    hint: 'Me, a colleague, an agent — or Unassigned to leave it on the queue. Assigning does not claim it.',
    required: 'Choose who it goes to, or take it off whoever has it.',
  },

  /* The three free-text fields every `tasks_*` dialog asks for. */
  reason: {
    hint: 'A sentence is enough — it goes on the record and the client may read it.',
    required: 'Say why, in a sentence. It goes on the record.',
  },
  outcome: {
    label: 'What was done',
    hint: 'A sentence for whoever reads this later. References in it are recorded against the task.',
    required: 'Say what was done, in a sentence.',
  },
  instructions: {
    label: 'What to do',
    hint: 'Written for somebody with no context — an agent or a colleague picking it up cold.',
  },
  acceptance: {
    label: 'How to know it is done',
    hint: 'What has to be true before anybody closes it.',
  },
  title: {
    hint: 'One line, as it will read on the board.',
    required: 'Give it a one-line title — it is what the board shows.',
  },
  context_refs: {
    label: 'Read these first',
    hint: 'One reference per line — WO-2026-008, REQ-119, FES-2026-047.',
  },
  /*
   * Words, not numbers — the owner's request of 17 Sep. `copy.options` relabels the
   * options `schemaFields` generates from the published 1–5 range, so the create
   * window and every "Change the priority" box read the same five words without a
   * second list of them anywhere. The band a task gets when nobody says is Medium.
   */
  priority: {
    hint: 'Urgent is the most pressing. Medium is what a task gets if you leave it.',
    options: PRIORITY_WORD,
  },
  money_ref: {
    label: 'The money it is about',
    hint: 'The work order, extra or invoice this task’s money belongs to. Naming one makes the task reviewed by a person whatever the box above says.',
  },
  /*
   * The same two fields on the window that **edits** them, where the amount had only
   * the tool's own words: "What it is worth, or null to take the money tag off it."
   * `null` is what the wire carries and never what a person does — they empty the
   * box, and the form is what sends the `null` (`schemaForm.ts` `clearedOf`). So each
   * line says how the thing is taken off, in the reader's terms.
   *
   * Keyed per action, because on New task an empty box is simply a task with no
   * money, and "the box above" there is the approval switch — here it would be the
   * amount, which is not what that sentence is about.
   */
  'tasks_update.money': {
    hint: 'What it is worth, for the board’s money tag. Leave the amount empty to take the tag off.',
  },
  'tasks_update.money_ref': {
    hint: 'The work order, extra or invoice this task’s money belongs to. Naming one makes the task reviewed by a person. Leave it blank to take the reference off.',
  },
  waiting_on: {
    label: 'Waiting on',
    hint: 'Whose move it is while it is blocked.',
    options: { client: 'Them', staff: 'Us', external: 'A third party' },
    required: 'Say whose move it is.',
  },
  minutes: {
    label: 'Minutes',
    hint: 'How long it took, in minutes. A correction is another entry — nothing here is edited.',
    required: 'How many minutes? A correction later is another entry.',
  },
  billable_kind: {
    label: 'What it draws on',
    hint: 'Which budget these minutes draw on — their package, a work order, or the studio.',
    options: {
      retainer: 'Their monthly package',
      project: 'A work order',
      account: 'We absorb it',
    },
    required: 'Say what these minutes draw on.',
  },
  body: {
    label: 'What to say',
    hint: 'Written on the task — not email. Pick who may read it below.',
    required: 'Write the comment. An empty one says nothing to anybody.',
  },
  visibility: {
    label: 'Who can see it',
    hint: 'Studio only stays internal. Client-visible marks it fit for the client to read — no client page shows a task thread yet.',
    options: {
      internal: 'Studio only',
      client: 'Client-visible (no client page yet)',
    },
    required: 'Choose who may read this comment.',
  },
  /*
   * The work-order screen's own three, session 24's `/studio/work-orders/$ref`.
   *
   * `consent_file_id` is the one field on that screen still asking for an id, and
   * that is a **gap rather than a choice**: a picker wants `files_list` scoped to the
   * client, and `ActionField` is not told which client a dialog was opened from — the
   * page passes it through `fixed`, which the renderer never sees. Threading it is a
   * change to `ActionForm`'s contract and belongs to whoever needs the second one;
   * until then the hint says where the id is, which is one click away on the same
   * client's Files section. The session report carries it.
   */
  consent_file_id: {
    label: 'The consent file',
    hint: 'The file holding their written approval — an email, a signed page, a message. Upload it to this client’s files first; its id is on the file’s own row.',
    required:
      'Name the file holding their written consent. There is no offline approval without one.',
  },
  approver_name: {
    label: 'Who approved it',
    hint: 'Their name, exactly as the consent says it. This record is the authority for the charge.',
    required: 'Name whoever approved it, as the consent says it.',
  },
  /*
   * "Leave it blank for now" read as a developer's note-to-self — a thing to come
   * back to — rather than as what it is: leaving it blank records the approval at
   * this moment. The two readings are opposites on a field that is the authority for
   * a charge, so the sentence names the consequence instead of the state.
   */
  approved_at: {
    label: 'When they approved it',
    hint: 'The day and time they said yes, in your own zone. Left blank, it is recorded as now.',
  },
  output_refs: {
    label: 'What it produced',
    hint: 'One reference per line — the files or deliverables this produced. It goes on the audit row.',
  },
  milestone_seq: {
    label: 'Which milestone',
    hint: 'Counting from 1. Milestones are delivered in order and the last one delivers the work order itself.',
  },
  /*
   * B-3. The box is **ticked by default** (`work_orders_propose`'s own schema says
   * `default: true`) and the line under it described only the unticked state — "Off,
   * and every milestone invoices on delivery like the rest" — directly beneath a
   * switch that was on. A reader who takes the sentence at face value leaves it as it
   * is and invoices a deposit they did not mean to. A switch's words say what **both**
   * positions do, in the order the switch is in.
   */
  deposit_on_approval: DEPOSIT_ON_APPROVAL,
  scope: {
    label: 'Scope',
    hint: 'What it covers, in the words the client will read on the quote.',
  },
  timing: {
    label: 'How soon it is wanted',
    hint: 'Rush is not a surcharge — it changes what we can promise, not what it costs.',
    options: { no_rush: 'No rush', this_month: 'This month', this_week: 'This week' },
  },

  /* ------------------------------------------------- the references and the ids
   *
   * Sixty-one fields across the catalogue name a row, and every one of their
   * published descriptions ends with the read that answers it — "`tasks_list` is how
   * to find one", "from `clients_get`", "`work_orders_list` is how to find one". That
   * is the right sentence for an agent choosing its next call and the wrong one for a
   * studio member, who reached this dialog **from the row** and, on almost every
   * screen, never sees the field at all: the page answered it through `fixed`.
   *
   * So each says what the thing is and where its identifier is printed in the portal,
   * and none of them names a tool.
   */

  work_order_ref: {
    label: 'Work order',
    hint: 'The project or the extra, by its reference — WO-2026-008 for a project, REQ-119 for an extra.',
    required: 'Name the work order this is about, by its reference.',
  },
  task_ref: {
    label: 'Task',
    hint: 'The task, by its reference — TSK-1042.',
    required: 'Name the task this is about, by its reference.',
  },
  invoice_ref: {
    label: 'Invoice',
    hint: 'The invoice, by its reference — FES-2026-047.',
    required: 'Name the invoice, by its reference.',
  },
  call_ref: {
    label: 'Call',
    hint: 'The call, by its reference — CALL-2026-091.',
    required: 'Name the call, by its reference.',
  },
  plan_ref: {
    label: 'Content plan',
    hint: 'The plan, by the month it covers — PLAN-2026-09.',
    required: 'Name the content plan, by its reference.',
  },
  agent: {
    label: 'Agent',
    hint: 'The agent, by the name it was created under — lowercase and hyphenated, like “content-drafter”.',
    required: 'Name the agent, exactly as it was created.',
  },
  widget: {
    label: 'Widget',
    hint: 'The widget, by its key — wgt_chat_0e2d. It is printed on the widget’s own row.',
    required: 'Name the widget by the key on its row.',
  },
  widget_type: {
    label: 'Kind of widget',
    hint: 'What it does — a chatbot, a booking widget. One of the kinds on the widget rate card.',
    required: 'Say what kind of widget it is.',
  },
  file_id: {
    label: 'File',
    hint: 'The file, by the id on its own row in this client’s files.',
    required: 'Name the file, by the id on its row.',
  },
  member_id: {
    label: 'The seat',
    hint: 'The seat’s id, off their row in this client’s people — not their user id, and not their email address.',
    required: 'Name the seat, by the id on their row.',
  },
  invitation_id: {
    label: 'The invitation',
    hint: 'The invitation’s id, off its own row — not the address it was sent to.',
    required: 'Name the invitation, by the id on its row.',
  },
  item_id: {
    label: 'Which piece',
    hint: 'The piece’s id, off its row in the plan.',
    required: 'Name the piece, by the id on its row.',
  },
  expense_id: {
    label: 'The expense',
    hint: 'The row being corrected, by its id.',
    required: 'Name the expense row, by its id.',
  },
  email_id: {
    label: 'The email',
    hint: 'The outbox row, by its id. Resending writes a new row; nothing is rewritten.',
    required: 'Name the outbox row, by its id.',
  },
  payment: {
    label: 'Payment',
    hint: 'The payment, by its id.',
    required: 'Name the payment, by its id.',
  },
  'payments_match.payment': {
    label: 'The credit',
    hint: 'Money that has arrived and has not been matched to anything yet.',
  },
  'payments_park.payment': {
    label: 'The credit',
    hint: 'Money that has arrived and has not been matched to anything yet.',
  },
  'payments_book_income.payment': {
    label: 'The credit',
    hint: 'Money waiting in the review queue — unmatched or parked. All of it is booked.',
  },
  'payments_book_income.amount': {
    label: 'Amount',
    hint: 'The whole credit, as the queue shows it. Anything else is refused.',
  },
  'payments_refund.payment': {
    label: 'The payment being reversed',
    hint: 'A refund is a new row against it. Nothing on the original is edited.',
  },

  /* ------------------------------------------------------- the account's terms */

  /*
   * The sentence the header of this file quotes. "This is how every tool addresses a
   * client; there is no UUID in the API surface" is a true and useful thing to tell an
   * agent, and it was printed at a studio member opening a brand-new account — who
   * needs to know that this is the name they will see on every screen afterwards and
   * that they cannot change it.
   */
  slug: {
    /*
     * The label stays "Slug". It is jargon, and it is the jargon this portal already
     * uses out loud — the terms step prints a *Slug* row and the URL of every client
     * page is one — so renaming it here would leave one screen calling it something
     * the next screen does not.
     */
    hint: 'What this account is called everywhere in the portal — lowercase letters, digits and hyphens, like “casaturca”. It cannot be changed afterwards.',
    required: 'Give the account a short lowercase name — “casaturca”.',
  },
  /*
   * `source` is the client-side pipeline the request arrived down, explained to
   * whoever built it: "composer — the free-text request form on the requests page.
   * catalogue — the ticked items on the billing page's rate card, which come with
   * codes and a preferred start." Two options say it in five words each, and the line
   * under them says the only thing a studio member actually needs: it changes nothing.
   *
   * Keyed per action, because `payments_record_manual` has a `source` too and its
   * five options are payment rails.
   */
  'work_orders_draft.source': {
    hint: 'How they asked. It changes nothing about the work — it is on the record.',
    options: { composer: 'They wrote it out', catalogue: 'They ticked it off the rate card' },
  },
  'work_orders_add_scope_change.source': {
    hint: 'How they asked. It changes nothing about the work — it is on the record.',
    options: { composer: 'They wrote it out', catalogue: 'They ticked it off the rate card' },
  },

  market_tier: {
    label: 'Market tier',
    hint: 'Which market the work targets: 1, 2 or 3. It follows the market the work is for, not where the company is registered, and it multiplies every price on the card.',
    required: 'Choose the market this work targets.',
  },
  seat_limit: {
    label: 'Seats',
    hint: 'How many people may hold a seat on the account. Five, if you leave it.',
  },
  /*
   * The label stays `labelOf('role')` — "Role" is a word everybody already has. What
   * was wrong was the line under it: four role names run together as a sentence, with
   * "(ARCHITECTURE.md, Identities and roles)" after them. A closed list explains
   * itself in its own options, which is where each of the four says what it can do.
   */
  role: {
    hint: 'It can be changed afterwards, and the change goes on the record.',
    options: {
      owner: 'Owner — approves money, manages seats',
      admin: 'Admin — approves money, manages seats',
      billing: 'Billing — sees and pays invoices',
      member: 'Member — comments and raises requests',
    },
    required: 'Choose what this person may do.',
  },

  /* --------------------------------------------------------------- the money
   *
   * B-1, and the most dangerous line in the portal. Every `$` action takes the amount
   * it commits as a check, and every one of those descriptions says **"in minor
   * units"** — because that is what the wire carries and the description is written
   * for whatever is putting things on the wire. The box is not the wire: `MoneyField`
   * renders `<input name="amount.major" placeholder="0.00">` and pre-fills it from
   * the row, so *Deliver a milestone* opened over a $600.00 milestone showed `600`
   * under a sentence telling the reader to type `60000`.
   *
   * Every one of these says what the **figure** is, in the words the rest of the
   * screen prints it in, and none of them says "minor units" — which
   * `actionCopy.test.ts` now refuses outright, for this field and for every other.
   */

  money_limit: {
    label: 'Money limit',
    hint: 'The most one money action may commit before it becomes a review task instead. Zero — where every agent starts — means every one of them is reviewed.',
  },
  'work_orders_propose.amount': {
    label: 'What the quote comes to',
    hint: 'The figure the client will be approving. It is refused when it is not what the work order was priced at.',
  },
  'work_orders_deliver_milestone.amount': {
    label: 'What this milestone is worth',
    hint: 'The share of the total this milestone carries, and what its invoice will be for. It is refused when it is not what the schedule says.',
  },
  /*
   * B-4. A draft nobody has priced has no remainder to pre-fill and no figure a
   * reader could look up — the page prints "Agreed total —, Not priced" — and the
   * form refused an empty box with "Amount has to be an amount." The page fills in
   * zero now (`workOrder/Lifecycle.tsx`); this is the sentence that says why zero is
   * the right answer rather than a guess.
   */
  'work_orders_cancel.amount': {
    label: 'What is left unbilled',
    hint: 'The agreed total, less every milestone already invoiced. Nothing priced and nothing invoiced makes it nothing — zero is the answer, not a guess.',
  },
  'retainers_pause.amount': {
    label: 'What it costs a month',
    hint: 'The retainer’s monthly figure, as a check. It is refused when it is not what the retainer says.',
  },
  'retainers_resume.amount': {
    label: 'What it costs a month',
    hint: 'The retainer’s monthly figure, as a check. It is refused when it is not what the retainer says.',
  },
  'retainers_schedule_cancellation.amount': {
    label: 'What it costs a month',
    hint: 'The retainer’s monthly figure, as a check. It is refused when it is not what the retainer says.',
  },
  'credits_adjust.amount': {
    label: 'The change',
    hint: 'How much to add, in the client’s own billing currency. A negative figure takes credit off.',
  },
  /*
   * The published description of this one names `invoices_resend`, which is a tool
   * name and is exactly what this file exists to keep off a person's screen. It went
   * unnoticed because `notify` was added to `invoices_issue` after the last
   * `pnpm world:record`, so the catalogue this test walks did not have the field at
   * all until session 35 re-recorded the world — which is the drift a recorded
   * fixture is for.
   *
   * The sentence says the same three things in the studio's own words: the invoice is
   * real, nothing is emailed, and there is a way to email it later without naming the
   * tool that does it.
   */
  'invoices_issue.notify': {
    label: 'Email it to the client',
    hint: QUIET_INVOICE,
  },
  /*
   * The same flag on the two other doors an invoice is issued through — a signup and a
   * billing run — with the same sentence, because it is the same three facts. Both
   * published descriptions name `invoices_resend` as the one above does.
   */
  'retainers_start.notify': {
    label: 'Email the signup invoice to the client',
    hint: QUIET_INVOICE,
  },
  'billing_run.notify': {
    label: 'Email the invoice to the client',
    hint: `${QUIET_INVOICE} A preview run emails nothing either way.`,
  },
  'invoices_charge.amount': {
    label: 'What is outstanding',
    hint: 'What is still owed on the invoice, in its own currency. It is refused when it is not exactly that.',
  },
  'invoices_retry_charge.amount': {
    label: 'What is outstanding',
    hint: 'What is still owed on the invoice, in its own currency. It is refused when it is not exactly that.',
  },
  /*
   * Session 44. The published description sends the reader to `invoices_get` for the
   * figure — the right sentence for an agent, and a tool name at a person who opened
   * this from the invoice's own row. The figure is on that row's Write off dialog, and
   * on the invoice; what this says is what the figure does.
   */
  'invoices_write_off.amount': {
    label: 'What to write off',
    hint: 'The part of what is still owed that the studio will not collect, in the invoice’s own currency. All of what is outstanding settles the invoice; more than that is refused.',
  },
  'invoices_write_off.reason': WRITE_OFF_REASON,
  attempt: {
    label: 'Which attempt',
    hint: 'Counting the first charge as 1. The invoice’s own payment history says which this is.',
  },
  'widgets_set_cap.worst_case': {
    label: 'What the cap is worth at most',
    hint: 'The most that cap could cost in a month: the units past what is included, at the unit price. It is refused when it is not what the widget comes to.',
  },
  'payments_match.hold_on_account': {
    label: 'Left on account',
    hint: 'What is deliberately not allocated. It becomes a credit on their account that a later match can spend — it is not prepaid widget credit, which is a different thing.',
  },

  /* ---------------------------------------------------------- the rate card */

  'catalog_update_price.block_amount': {
    label: 'Price per block',
    hint: 'The new Tier 1 price for one block a month. Leave it empty to make the service quote-only from that day, which is not the same as free.',
  },
  'catalog_create_widget_type.unit_amount': {
    label: 'Price per unit',
    hint: 'What one billable unit costs — 0.12 is twelve cents, 0 for a kind sold by the month or free, which then includes nothing and has no cap. New widgets start here; a live one keeps the price it was created at.',
  },
  'catalog_update_widget_type.unit_amount': {
    label: 'Price per unit',
    hint: 'What one billable unit costs — 0.12 is twelve cents, 0 for a kind sold by the month or free, which then includes nothing and has no cap. It is the price new widgets start on; a live one keeps the price it was created at.',
  },
  'catalog_create_widget_type.monthly_amount': {
    label: 'Monthly fee',
    hint: 'What a widget of this kind costs each month whatever it does — 5.00 for the blog. Empty or 0 is no fee. Billed in arrears for each full month a widget is live; the first, partial month is free.',
  },
  'catalog_update_widget_type.monthly_amount': {
    label: 'Monthly fee',
    hint: 'What a widget of this kind costs each month whatever it does — 0 for no fee. New widgets start on it; a live one keeps the fee it was created at.',
  },

  /* ------------------------------------------- a client's own price (46d) */

  'widgets_set_client_price.unit_amount': {
    label: 'Price per unit',
    hint: 'In the client’s own currency, billed exactly as typed. 0 for a kind sold by the month or given free, which then includes nothing and has no cap.',
  },
  'widgets_set_client_price.monthly_amount': {
    label: 'Monthly fee',
    hint: 'What each full month costs this client whatever the widget does — 0 for none. All four at 0 makes the kind free for them.',
  },
  'widgets_set_client_price.included_qty': {
    label: 'Included each month',
    hint: 'Units before anything is billed. 0 when there is no price per unit.',
  },
  'widgets_set_client_price.monthly_cap': {
    label: 'Monthly cap',
    hint: 'The ceiling on units a month, never under what is included. 0 when there is no price per unit.',
  },
  'widgets_set_client_price.reason': {
    label: 'Why this client pays this',
    hint: 'Kept with the price for good and shown beside it here — what was agreed, and when.',
    required: 'Say what was agreed — a sentence is enough.',
  },
  'widgets_remove_client_price.reason': {
    label: 'Why it ends',
    hint: 'Kept in the client’s price history beside the price it ends.',
    required: 'Say why the client goes back to the card.',
  },
  'catalog_update_extra_unit.unit_amount': {
    label: 'Price per unit',
    hint: 'The new Tier 1 price for one unit. Extras sit about a tenth above the block-implied unit, so heavy users upgrade rather than accumulate extras.',
  },
  'catalog_update_extra_unit.code': {
    label: 'Unit',
    hint: 'What it is counted in — “article”, “hour”, “reel”. A code the service does not have yet creates one.',
    required: 'Say what the unit is called.',
  },
  'catalog_update_widget_type.code': {
    label: 'Widget type',
    hint: 'The widget type, by its code.',
    required: 'Say which widget type.',
  },
  'work_orders_price.unit': {
    label: 'Unit',
    hint: 'What it is counted in — “reel”, “hour”, “website_extra_page”. It has to be one the service beside it has.',
    required: 'Say what this line is counted in.',
  },
  pilot: {
    label: 'Provisional price',
    hint: 'The portal prints a “pilot” tag beside the price while it is still being decided.',
  },
  content_type: {
    label: 'What it is',
    /* The four options say it. A muted line restating them is the padding §2.8's one
       line exists to avoid. */
    hint: null,
    options: { reel: 'Reel', carousel: 'Carousel', static: 'Static post', story: 'Story' },
    required: 'Choose what kind of piece it is.',
  },

  /* ------------------------------------------------------- the hosted widgets
   *
   * Session 46's blog and forms tools describe a reference by the list tool it comes
   * from — "blog_list_posts lists them" — which is right for an agent reading the
   * catalogue and no use to a person. A person meets a post in the blog workspace
   * (`pages/blog/`) and a submission in the inbox; these are the words for a form that
   * asks for one anyway.
   */
  post: {
    label: 'Post',
    hint: 'The post, by its reference — PST-2026-0001 — as the blog’s posts page shows it.',
  },
  revision: {
    label: 'Revision',
    hint: 'The revision, by its number in the post’s history.',
  },
  receipt: {
    label: 'Submission',
    hint: 'The submission, by its receipt — rcpt_ and twenty characters — as the inbox shows it.',
  },
  'forms_update.form': {
    label: 'Form',
    hint: 'The form, by its key on the widget — contact, quote.',
  },
  'blog_update_author.key': {
    label: 'Author',
    hint: 'The author, by their key in the blog’s directory. The key never changes.',
  },
  'blog_remove_author.key': {
    label: 'Author',
    hint: 'The author, by their key in the blog’s directory. One a post names cannot be removed.',
  },

  /* ------------------------------------------------------------ the repeaters
   *
   * `add` is the button under the rows. Every one of these was `Add ${label}` over the
   * label the field is *drawn* with, which is how the Price-it form came to offer
   * **"Add add ons (optional)"**.
   */

  'work_orders_propose.milestones': {
    label: 'Milestones',
    hint: 'The schedule, in order, with the shares adding up to 100. Leave it empty for 50% on approval and 50% on delivery; an extra takes none.',
    add: 'Add a milestone',
  },
  'work_orders_price.add_ons': { label: 'Add-ons', add: 'Add an add-on' },
  'work_orders_price.units': { label: 'Units', add: 'Add a unit' },
  'invoices_draft.lines': { label: 'Lines', add: 'Add a line' },
  'payments_match.allocations': { label: 'Where it goes', add: 'Allocate to another invoice' },
  'catalog_create_package.items': { label: 'Blocks', add: 'Add a block' },
  'content_plans_draft.items': { label: 'Pieces', add: 'Add a piece' },
  'calls_set_availability_rules.windows': { label: 'Windows', add: 'Add a day' },
  'calls_add_notes.actions': { label: 'What it left open', add: 'Add an action' },
  'notifications_notify_staff.sections': { label: 'Sections', add: 'Add a section' },
  'forms_create.fields': { label: 'Fields', add: 'Add a field' },
  'forms_update.fields': { label: 'Fields', add: 'Add a field' },

  /*
   * Session 48. The published hint for a mockup ref tells an agent which tools find one;
   * a person filling the field in is already looking at the mockup.
   */
  mockup: { label: 'Mockup', hint: 'Its reference — MCK-2026-001.' },
  'mockups_resolve_comment.comment_id': { label: 'Comment', hint: null },
  // Session 55: the thread the Reopen button sat in.
  'mockups_reopen_comment.comment_id': { label: 'Comment', hint: null },
  // Session 49: the comment is the one the Hide button sat beside.
  'mockups_hide_comment.comment_id': { label: 'Comment', hint: null },
  'mockups_hide_comment.reason': {
    label: 'Why',
    hint: 'For the record — spam, or not something for the client to read.',
  },

  idempotency_key: {},
}

/**
 * The words for one field: the action's own entry, then the field's, then nothing.
 *
 * Merged per property rather than "the first key that exists", so
 * `"tasks_create.client"` can give a label and still inherit the shared hint.
 */
export function copyFor(action: string, field: string): FieldCopy {
  return { ...FIELD_COPY[field], ...FIELD_COPY[`${action}.${field}`] }
}
